import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { environment } from 'src/environments/environment';
import { getProfileToken, getAccessToken } from '@core/auth/utils/session-storage.util';
import { NewChatNotification, ChatMessageDTO, TypingIndicator } from '@core/models/chat.models';

type SockJsCtor = new (url: string, _reserved?: any, options?: any) => WebSocket;

const resolveSockJsCtor = (): SockJsCtor => {
  const maybeModule = SockJS as unknown as { default?: SockJsCtor } & SockJsCtor;
  return maybeModule.default ?? (maybeModule as SockJsCtor);
};

export interface ChatWsMessage {
  chatId: string;
  message: ChatMessageDTO;
}

export interface ChatWsTyping {
  chatId: string;
  typing: TypingIndicator;
}

@Injectable({
  providedIn: 'root'
})
export class ChatNotificationWsService implements OnDestroy {
  private readonly sockJsCtor = resolveSockJsCtor();
  private stompClient: Client | null = null;
  private newChatSubscription: StompSubscription | null = null;
  private chatSubscriptions = new Map<string, StompSubscription[]>();
  private isConnecting = false;
  private pendingChatIds: string[] = [];

  private connectedSubject = new BehaviorSubject<boolean>(false);
  private newChatSubject = new Subject<NewChatNotification>();
  private chatMessageSubject = new Subject<ChatWsMessage>();
  private chatTypingSubject = new Subject<ChatWsTyping>();

  connected$ = this.connectedSubject.asObservable();
  newChat$ = this.newChatSubject.asObservable();
  chatMessage$ = this.chatMessageSubject.asObservable();
  chatTyping$ = this.chatTypingSubject.asObservable();

  connect(): void {
    if (!environment.agentsEnabled) {
      console.info('[STOMP] Agentes IA deshabilitados: no se iniciará conexión WebSocket.');
      return;
    }

    if (this.stompClient?.connected || this.isConnecting) return;

    this.isConnecting = true;
    const token = getProfileToken() || getAccessToken();

    this.stompClient = new Client({
      webSocketFactory: () => new this.sockJsCtor(environment.wsUrl),
      connectHeaders: {
        Authorization: token ? `Bearer ${token}` : ''
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      debug: (msg) => {
        if (!environment.production) {
          console.debug('[STOMP]', msg);
        }
      }
    });

    this.stompClient.onConnect = () => {
      this.isConnecting = false;
      this.connectedSubject.next(true);
      this.subscribeToNewChat();
      this.subscribePendingChats();
    };

    this.stompClient.onStompError = () => {
      this.isConnecting = false;
      this.connectedSubject.next(false);
    };

    this.stompClient.onWebSocketClose = () => {
      this.isConnecting = false;
      this.connectedSubject.next(false);
    };

    this.stompClient.activate();
  }

  private subscribeToNewChat(): void {
    if (!this.stompClient?.connected) return;

    this.newChatSubscription?.unsubscribe();

    this.newChatSubscription = this.stompClient.subscribe(
      '/user/queue/new-chat',
      (message: IMessage) => {
        try {
          const data: NewChatNotification = JSON.parse(message.body);
          if (data.chatId && data.solicitudId) {
            this.newChatSubject.next(data);
          }
        } catch (e) {
          console.error('[STOMP] Error parsing new-chat:', e);
        }
      }
    );
  }

  subscribeToChat(chatId: string): void {
    if (!chatId) return;

    if (this.chatSubscriptions.has(chatId)) return;

    if (!this.stompClient?.connected) {
      if (!this.pendingChatIds.includes(chatId)) {
        this.pendingChatIds.push(chatId);
      }
      return;
    }

    this.doSubscribeToChat(chatId);
  }

  private doSubscribeToChat(chatId: string): void {
    if (!this.stompClient?.connected || this.chatSubscriptions.has(chatId)) return;

    const subs: StompSubscription[] = [];

    subs.push(
      this.stompClient.subscribe(
        `/user/queue/chat/${chatId}`,
        (message: IMessage) => {
          try {
            const parsed: ChatMessageDTO = JSON.parse(message.body);
            this.chatMessageSubject.next({ chatId, message: parsed });
          } catch (e) {
            console.error('[STOMP] Error parsing chat message:', e);
          }
        }
      )
    );

    subs.push(
      this.stompClient.subscribe(
        `/user/queue/chat/${chatId}/typing`,
        (message: IMessage) => {
          try {
            const parsed: TypingIndicator = JSON.parse(message.body);
            this.chatTypingSubject.next({ chatId, typing: parsed });
          } catch (e) {
            console.error('[STOMP] Error parsing typing:', e);
          }
        }
      )
    );

    this.chatSubscriptions.set(chatId, subs);
  }

  unsubscribeFromChat(chatId: string): void {
    const subs = this.chatSubscriptions.get(chatId);
    if (subs) {
      subs.forEach(s => s.unsubscribe());
      this.chatSubscriptions.delete(chatId);
    }
    this.pendingChatIds = this.pendingChatIds.filter(id => id !== chatId);
  }

  private subscribePendingChats(): void {
    const pending = [...this.pendingChatIds];
    this.pendingChatIds = [];
    pending.forEach(chatId => this.doSubscribeToChat(chatId));
  }

  private unsubscribeAllChats(): void {
    this.chatSubscriptions.forEach(subs => subs.forEach(s => s.unsubscribe()));
    this.chatSubscriptions.clear();
    this.pendingChatIds = [];
  }

  disconnect(): void {
    this.newChatSubscription?.unsubscribe();
    this.newChatSubscription = null;
    this.unsubscribeAllChats();
    if (this.stompClient?.connected) {
      this.stompClient.deactivate();
    }
    this.stompClient = null;
    this.isConnecting = false;
    this.connectedSubject.next(false);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
